import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { userService } from './userService';

export interface BillingInfo {
  status: 'active' | 'trialing' | 'expired';
  trial_days_remaining?: number | null; // null if not on trial
  requests_remaining?: number; // AI requests remaining
  requests_quota?: number; // Total AI requests quota (typically 100)
}

export interface CheckoutSessionResponse {
  url: string;
  session_id: string;
}

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment:
// - If REACT_APP_API_URL is provided at build time, use it
// - Else if running on localhost or the known dev S3 website, use the dev API
// - Else default to prod API
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

// Cache for billing data
let cachedBillingInfo: BillingInfo | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

class BillingService {
  /**
   * Get auth token from Cognito
   */
  // Using centralized userService.getAuthToken() instead of duplicate code



  private isCacheValid(): boolean {
    return Date.now() - cacheTimestamp < CACHE_DURATION;
  }

  clearCache(): void {
    cachedBillingInfo = null;
    cacheTimestamp = 0;
  }

  /**
   * Get current subscription information
   */
  async getCurrentSubscription(): Promise<BillingInfo | null> {
    if (cachedBillingInfo && this.isCacheValid()) {
      return cachedBillingInfo;
    }

    try {
      const token = await userService.getAuthToken();
      
      // Include session_id if present (server may fulfill AI addon)
      const currentUrl = new URL(window.location.href);
      const sessionId = currentUrl.searchParams.get('session_id');
      const url = new URL(`${API_BASE_URL}/billing/subscription`);
      if (sessionId) url.searchParams.set('session_id', sessionId);

      const response = await axios.get<BillingInfo>(url.toString(), {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      });
      
      cachedBillingInfo = response.data;
      cacheTimestamp = Date.now();
      // Clean up session_id from URL after successful fetch
      if (sessionId) {
        currentUrl.searchParams.delete('session_id');
        window.history.replaceState({}, document.title, currentUrl.toString());
      }
      return cachedBillingInfo;
    } catch (error: any) {
      console.error('[BillingService] Error fetching subscription:', error);
      
      // Return default trial plan if no subscription found
      if (error?.response?.status === 404) {
        return {
          status: 'trialing',
          trial_days_remaining: 14
        };
      }
      
      return null;
    }
  }



  /**
   * Create Stripe Customer Portal session for subscription management
   */
  async createCustomerPortalSession(returnUrl?: string): Promise<{url: string; session_id: string}> {
    try {
      const token = await userService.getAuthToken();
      
      console.log('[BillingService] Creating customer portal session');
      
      const requestData = {
        return_url: returnUrl || `${window.location.origin}/billing`
      };
      
      console.log('[BillingService] Portal request data:', requestData);
      
      const response = await axios.post<{portal_url: string; session_id: string}>(
        `${API_BASE_URL}/billing/customer-portal`, 
        requestData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[BillingService] Portal response:', response.data);
      
      // Clear cache since subscription may change in the portal
      this.clearCache();
      
      return {
        url: response.data.portal_url,
        session_id: response.data.session_id
      };
    } catch (error: any) {
      console.error('[BillingService] Error creating customer portal session:', error);
      if (error.response) {
        console.error('[BillingService] Response status:', error.response.status);
        console.error('[BillingService] Response data:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Create Stripe checkout session for plan upgrade
   */
  async createCheckoutSession(quantity?: number): Promise<CheckoutSessionResponse> {
    try {
      const token = await userService.getAuthToken();
      
      console.log('[BillingService] Creating checkout session:', { quantity });
      
      const requestData = {
        success_url: `${window.location.origin}/billing`,
        cancel_url: `${window.location.origin}/billing`,
        ...(quantity && { quantity })
      };
      
      console.log('[BillingService] Request data:', requestData);
      
      const response = await axios.post<{checkout_url: string; session_id: string}>(
        `${API_BASE_URL}/billing/checkout`, 
        requestData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('[BillingService] Checkout response:', response.data);
      
      // Clear cache since subscription may change
      this.clearCache();
      
      return {
        url: response.data.checkout_url,
        session_id: response.data.session_id
      };
    } catch (error: any) {
      console.error('[BillingService] Error creating checkout session:', error);
      if (error.response) {
        console.error('[BillingService] Response status:', error.response.status);
        console.error('[BillingService] Response data:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Create checkout session for AI add-on requests
   */
  async createAiAddonCheckout(quantity?: number): Promise<CheckoutSessionResponse> {
    try {
      const token = await userService.getAuthToken();

      const requestData: any = {
        success_url: `${window.location.origin}/billing`,
        cancel_url: `${window.location.origin}/billing`,
      };
      if (quantity) requestData.quantity = quantity;

      const response = await axios.post<{checkout_url: string; session_id: string}>(
        `${API_BASE_URL}/billing/ai-addon-checkout`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // We'll refresh after returning from Stripe by refetching subscription
      this.clearCache();

      return {
        url: response.data.checkout_url,
        session_id: response.data.session_id,
      };
    } catch (error: any) {
      console.error('[BillingService] Error creating AI add-on checkout:', error);
      throw error;
    }
  }

  /**
   * Fulfill AI add-on after returning from Stripe (session_id in URL)
   */
  async fulfillAiAddon(checkoutSessionId: string): Promise<{ added_requests: number; requests_remaining: number | null } | null> {
    try {
      const token = await userService.getAuthToken();
      const response = await axios.post<{ added_requests: number; requests_remaining: number | null }>(
        `${API_BASE_URL}/billing/ai-addon-checkout`,
        { action: 'fulfill', checkout_session_id: checkoutSessionId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      this.clearCache();
      return response.data;
    } catch (error) {
      console.error('[BillingService] fulfillAiAddon failed:', error);
      return null;
    }
  }
  /**
   * Fulfill AI add-on purchase after returning from Stripe (uses session_id)
   */
  // Removed fulfillAiAddon; rely on subscription sync to reflect changes

}

// Export singleton instance
export const billingService = new BillingService(); 